import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/lib/api-client';
import {
  adminBreakglassStart,
  adminBreakglassVerify,
  adminDevSignIn,
  adminGoogleSignIn,
  adminInvite,
  adminLogout,
  adminMe,
  getAdminAuthConfig,
  continueDossierRun,
  forceFailDossierRun,
  retryDossierRun,
  forceLogoutUser,
  getAdminCompanies,
  getAdminCompanyDetail,
  getAdminVaultCompanies,
  getAdminVaultUploads,
  promoteFolder,
  getCompanyProducts,
  promoteVaultUpload,
  unpromoteVaultUpload,
  getAdminDossierRuns,
  getAdminExtractionRuns,
  getAdminSupportingDocConversions,
  getAdminInvites,
  getStaffSessions,
  signOutStaffMember,
  getAdminClinicalRuns,
  getAdminPredicateRuns,
  getAdminStrategyRuns,
  getAppSettings,
  getExtractionStats,
  retryExtractionProposal,
  retrySupportingDocConversion,
  getClinicalConcurrency,
  getDossierConcurrency,
  getPredicateConcurrency,
  getStrategyConcurrency,
  forceFailClinicalRun,
  retryClinicalRun,
  forceFailPredicateRun,
  retryPredicateRun,
  getArchiveImportConcurrency,
  setArchiveImportConcurrency,
  getAdminArchiveImportRuns,
  forceFailArchiveImport,
  retryArchiveImport,
  forceFailStrategyRun,
  retryStrategyRun,
  resendUserInvite,
  revokeAdminInvite,
  setCompanyAccess,
  setCompanyTrack,
  setUserAccess,
  setClinicalConcurrency,
  setDossierConcurrency,
  setPredicateConcurrency,
  setStrategyConcurrency,
  updateAppSettings,
  productsOverLimitFromConflict,
  getMcpObservability,
  getCorpusObservability,
  fetchCompanyBilling,
  provisionCompanyBilling,
  getBillingHealth,
  setBillingOverride,
} from './api';
import type { ProvisionBillingArgs } from './api';
import type { AdminAppSettingsUpdate } from './api';
import type { ClinicalAgentKind } from './api';

/**
 * Hooks for the chrome-less admin surface. Mirrors the customer-facing
 * useMe / useLogin / useLogout shape but against /api/admin/* endpoints.
 *
 * The admin "me" query resolves to ``null`` on 401 so the admin-login
 * page can branch without bouncing itself back via the api-client's
 * 401 interceptor.
 */

const ADMIN_ME_KEY = ['admin', 'me'] as const;

/** The accessible hint shown on every control a viewer can't use — spelled out
 *  once so its wording never drifts between the ~20 places it's applied. */
export const VIEW_ONLY_HINT = 'View only — ask an admin';

/** True for a full admin (ADMIN_EMAILS); false for a viewer or while signed
 *  out. Pages render for both — this only decides whether a control that
 *  fires a write is enabled, so a viewer never clicks into a 403. */
export function useAdminCanEdit(): boolean {
  return useAdminMe().data?.role === 'admin';
}

/** One `disabled` + `title` pair for every admin control that fires a write
 *  (or the vault download) — that pair was copy-pasted at ~30 call sites
 *  before this existed. `gate(ownDisabled, ownTitle)` folds the control's own
 *  disabled condition and title in with the viewer gate: a viewer gets
 *  `disabled: true` and the `VIEW_ONLY_HINT` title regardless of `ownDisabled`
 *  / `ownTitle`; a full admin gets exactly `ownDisabled` / `ownTitle`
 *  untouched. Spread the result onto the control: `<Button {...gate(...)}>`.
 *  `canEdit` is also returned for the handful of non-button cases (a query's
 *  `enabled`, a conditional badge) that don't fit the spread shape. */
export function useViewOnlyGate() {
  const canEdit = useAdminCanEdit();
  const gate = (
    ownDisabled = false,
    ownTitle?: string,
  ): { disabled: boolean; title: string | undefined } => ({
    disabled: ownDisabled || !canEdit,
    title: canEdit ? ownTitle : VIEW_ONLY_HINT,
  });
  return { canEdit, gate };
}

export function useAdminMe(enabled = true) {
  return useQuery({
    queryKey: ADMIN_ME_KEY,
    queryFn: async () => {
      try {
        const res = await adminMe();
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

const ADMIN_AUTH_CONFIG_KEY = ['admin', 'auth-config'] as const;

export function useAdminAuthConfig() {
  return useQuery({
    queryKey: ADMIN_AUTH_CONFIG_KEY,
    queryFn: getAdminAuthConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Wrapped (rather than passed straight through as `mutationFn: fn`) so the
// mutation-context object TanStack Query's mutate() appends as a second
// argument never reaches the api.ts call — that layer's functions have a
// single-argument signature and callers assert on it directly.
function useSignInMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => fn(args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_ME_KEY });
    },
  });
}

export const useAdminGoogleSignIn = () => useSignInMutation(adminGoogleSignIn);
export const useAdminDevSignIn = () => useSignInMutation(adminDevSignIn);
export const useAdminBreakglassVerify = () => useSignInMutation(adminBreakglassVerify);

export function useAdminBreakglassStart() {
  return useMutation({
    mutationFn: (args: { email: string; password: string }) => adminBreakglassStart(args),
  });
}

export function useAdminLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminLogout,
    onSuccess: () => {
      void qc.removeQueries({ queryKey: ADMIN_ME_KEY });
    },
  });
}

const ADMIN_INVITES_KEY = ['admin', 'invites'] as const;

export function useAdminInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminInvite,
    // A new invite should appear in the pending-invites list immediately.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_INVITES_KEY });
    },
  });
}

export function useAdminInvites() {
  return useQuery({ queryKey: ADMIN_INVITES_KEY, queryFn: getAdminInvites, retry: false });
}

export function useRevokeAdminInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revokeAdminInvite,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_INVITES_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_STAFF_SESSIONS_KEY = ['admin', 'staff-sessions'] as const;

/** Signed-in staff. Admin-only on the server, so a viewer passes `enabled: false`. Short
 *  staleTime + refocus refetch: it's a "who's in right now" view. */
export function useStaffSessions(enabled: boolean) {
  return useQuery({
    queryKey: ADMIN_STAFF_SESSIONS_KEY,
    queryFn: getStaffSessions,
    enabled,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useSignOutStaffMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: signOutStaffMember,
    onSuccess: (_res, email) => {
      toast.success(`Signed out ${email}.`);
      void qc.invalidateQueries({ queryKey: ADMIN_STAFF_SESSIONS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_COMPANIES_KEY = ['admin', 'companies'] as const;

const COMPANIES_PAGE_SIZE = 20;

export function useAdminCompanies(search = '') {
  return useInfiniteQuery({
    // Keyed by search so changing it starts a fresh page-1 query; the flip
    // mutation invalidates the ['admin','companies'] prefix, covering all terms.
    queryKey: [...ADMIN_COMPANIES_KEY, { search }],
    queryFn: ({ pageParam }) => getAdminCompanies(pageParam, COMPANIES_PAGE_SIZE, search),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    retry: false,
  });
}

export function useSetCompanyTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, track }: { id: number; track: 'non_training' | 'training' }) =>
      setCompanyTrack(id, track),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
    },
    onError: toastClientError,
  });
}

// The global MutationCache toasts 5xx/network and the app-wide duplicate-write 409,
// and stays silent on other 4xx (401/409 are handled elsewhere). Surface the
// client-actionable 4xx here (e.g. 400 "already signed up", 404) so an admin action
// never fails silently.
function toastClientError(err: unknown) {
  if (
    err instanceof ApiError &&
    err.status >= 400 &&
    err.status < 500 &&
    err.status !== 401 &&
    err.status !== 409
  ) {
    toast.error(err.message);
  }
}

export function useAdminCompanyDetail(id: number) {
  return useQuery({
    queryKey: [...ADMIN_COMPANIES_KEY, id],
    queryFn: () => getAdminCompanyDetail(id),
    retry: false,
  });
}

export function useForceLogoutUser(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => forceLogoutUser(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADMIN_COMPANIES_KEY, companyId] });
    },
    onError: toastClientError,
  });
}

/** Suspend or restore a whole company's access (durable block for a non-renewing
 *  company). Invalidates both the list and this company's detail. */
export function useSetCompanyAccess(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: 'active' | 'suspended') => setCompanyAccess(companyId, status),
    onSuccess: (res) => {
      toast.success(
        res.access_status === 'suspended'
          ? 'Company access suspended.'
          : 'Company access restored.',
      );
      void qc.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
      void qc.invalidateQueries({ queryKey: [...ADMIN_COMPANIES_KEY, companyId] });
    },
    onError: toastClientError,
  });
}

/** Suspend or restore a single user's access, independent of their company. */
export function useSetUserAccess(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }: { userId: number; status: 'active' | 'suspended' }) =>
      setUserAccess(userId, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADMIN_COMPANIES_KEY, companyId] });
    },
    onError: toastClientError,
  });
}

export function useResendUserInvite(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => resendUserInvite(userId),
    // Refetch the detail so the pending member's new expiry shows.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADMIN_COMPANIES_KEY, companyId] });
    },
    onError: toastClientError,
  });
}

/**
 * useAdminAuthGuard — bounce unauthenticated visitors back to /admin/login.
 *
 * Shared by the authed admin pages (dashboard, terms) so they all guard the
 * session identically: once the admin "me" query resolves to null, replace the
 * location with the login page. Returns the underlying query so callers can
 * gate rendering on it if they wish.
 */
export function useAdminAuthGuard() {
  const me = useAdminMe();
  useEffect(() => {
    if (me.isFetched && me.data == null) {
      window.location.replace('/admin/login');
    }
  }, [me.isFetched, me.data]);
  return me;
}

// ── Dossier ops ─────────────────────────────────────────────────────────────

const ADMIN_DOSSIER_RUNS_KEY = ['admin', 'dossier-runs'] as const;

/** In-flight dossier runs across all tenants; polls so the panel stays live. */
export function useAdminDossierRuns() {
  return useQuery({
    queryKey: ADMIN_DOSSIER_RUNS_KEY,
    queryFn: getAdminDossierRuns,
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useForceFailDossierRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: forceFailDossierRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_DOSSIER_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

export function useContinueDossierRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: continueDossierRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_DOSSIER_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

export function useRetryDossierRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryDossierRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_DOSSIER_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_DOSSIER_CONCURRENCY_KEY = ['admin', 'dossier-concurrency'] as const;

/** The concurrency dial (effective cap / override / running count); polls so
 *  the running count stays live alongside the runs table. */
export function useDossierConcurrency() {
  return useQuery({
    queryKey: ADMIN_DOSSIER_CONCURRENCY_KEY,
    queryFn: getDossierConcurrency,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Set (number) or clear (null) the runtime concurrency cap. Applies to the
 *  doorman immediately — no redeploy. */
export function useSetDossierConcurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setDossierConcurrency,
    onSuccess: (res) => {
      toast.success(
        res.override != null
          ? `Concurrency cap set to ${res.cap}.`
          : `Concurrency cap reset to the default (${res.env_default}).`,
      );
      void qc.invalidateQueries({ queryKey: ADMIN_DOSSIER_CONCURRENCY_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_PREDICATE_CONCURRENCY_KEY = ['admin', 'predicate-concurrency'] as const;

/** The predicate concurrency dial (effective cap / override / running count);
 *  polls so the running count stays live. */
export function usePredicateConcurrency() {
  return useQuery({
    queryKey: ADMIN_PREDICATE_CONCURRENCY_KEY,
    queryFn: getPredicateConcurrency,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Set (number) or clear (null) the runtime predicate concurrency cap. Applies to
 *  the worker immediately — no redeploy. */
export function useSetPredicateConcurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setPredicateConcurrency,
    onSuccess: (res) => {
      toast.success(
        res.override != null
          ? `Predicate concurrency cap set to ${res.cap}.`
          : `Predicate concurrency cap reset to the default (${res.env_default}).`,
      );
      void qc.invalidateQueries({ queryKey: ADMIN_PREDICATE_CONCURRENCY_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_SETTINGS_KEY = ['admin', 'app-settings'] as const;

/** The runtime tech-spec dials (extraction toggle + document upload cap).
 *  Static-ish config, so no polling — the admin sees their own change via the
 *  mutation's invalidate. */
export function useAppSettings() {
  return useQuery({
    queryKey: ADMIN_SETTINGS_KEY,
    queryFn: getAppSettings,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

const ADMIN_EXTRACTION_STATS_KEY = ['admin', 'extraction-stats'] as const;

/** Size + rough-cost distribution of extracted tech specs — the evidence line
 *  next to the extraction dials. Static-ish; no polling. */
export function useExtractionStats() {
  return useQuery({
    queryKey: ADMIN_EXTRACTION_STATS_KEY,
    queryFn: getExtractionStats,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Set the runtime tech-spec dials. Applies immediately — read fresh per request,
 *  no redeploy. */
export function useUpdateAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (update: AdminAppSettingsUpdate) => updateAppSettings(update),
    onSuccess: () => {
      toast.success('Settings saved.');
      void qc.invalidateQueries({ queryKey: ADMIN_SETTINGS_KEY });
      // Target-markets dials feed the customer-side pickers; refresh them so
      // an operator browsing the app in this session sees their own change
      // (other sessions ride the pickers' 5-minute staleTime).
      void qc.invalidateQueries({ queryKey: ['countries'] });
      void qc.invalidateQueries({ queryKey: ['product-types'] });
    },
    onError: (err, update) => {
      // Lowering a per-product limit below what real products already hold is refused
      // until it is acknowledged, so it can never happen by accident. Offer the
      // acknowledgement here rather than leaving the admin at a dead end.
      const over = productsOverLimitFromConflict(err);
      if (over) {
        toast.warning(over.message, {
          duration: 20_000,
          action: {
            label: 'Lower anyway',
            onClick: () => updateAppSettings({ ...update, confirm_over_limit: true }).then(
              () => {
                toast.success('Settings saved.');
                void qc.invalidateQueries({ queryKey: ADMIN_SETTINGS_KEY });
              },
              toastClientError,
            ),
          },
        });
        return;
      }
      toastClientError(err);
    },
  });
}

const ADMIN_PREDICATE_RUNS_KEY = ['admin', 'predicate-runs'] as const;

/** In-flight predicate runs across all tenants; polls so the panel stays live. */
export function useAdminPredicateRuns() {
  return useQuery({
    queryKey: ADMIN_PREDICATE_RUNS_KEY,
    queryFn: getAdminPredicateRuns,
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useForceFailPredicateRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: forceFailPredicateRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_PREDICATE_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

export function useRetryPredicateRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryPredicateRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_PREDICATE_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_ARCHIVE_IMPORT_CONCURRENCY_KEY = ['admin', 'archive-import-concurrency'] as const;

/** The archive-import concurrency dial (effective cap / override / importing count); polls so the
 *  importing count stays live. */
export function useArchiveImportConcurrency() {
  return useQuery({
    queryKey: ADMIN_ARCHIVE_IMPORT_CONCURRENCY_KEY,
    queryFn: getArchiveImportConcurrency,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Set (number) or clear (null) the runtime archive-import concurrency cap. Applies to the worker
 *  immediately — no redeploy. */
export function useSetArchiveImportConcurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setArchiveImportConcurrency,
    onSuccess: (res) => {
      toast.success(
        res.override != null
          ? `Imports-per-worker set to ${res.cap}.`
          : `Imports-per-worker reset to the default (${res.env_default}).`,
      );
      void qc.invalidateQueries({ queryKey: ADMIN_ARCHIVE_IMPORT_CONCURRENCY_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_ARCHIVE_IMPORT_RUNS_KEY = ['admin', 'archive-import-runs'] as const;

/** In-flight + recently-failed archive imports across all tenants (+ DLQ depth); polls so the
 *  panel stays live. Returns the full { runs, dlq_depth } payload. */
export function useAdminArchiveImportRuns() {
  return useQuery({
    queryKey: ADMIN_ARCHIVE_IMPORT_RUNS_KEY,
    queryFn: getAdminArchiveImportRuns,
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useForceFailArchiveImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: forceFailArchiveImport,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_ARCHIVE_IMPORT_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

export function useRetryArchiveImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryArchiveImport,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_ARCHIVE_IMPORT_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_EXTRACTION_RUNS_KEY = ['admin', 'extraction-runs'] as const;

/** In-flight + recently-failed tech-spec extractions across all tenants (plus the
 *  DLQ depth); polls so the panel stays live. */
export function useAdminExtractionRuns() {
  return useQuery({
    queryKey: ADMIN_EXTRACTION_RUNS_KEY,
    queryFn: getAdminExtractionRuns,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Retry a FAILED extraction — re-arms it for a fresh build and re-dispatches. */
export function useRetryExtractionProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryExtractionProposal,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_EXTRACTION_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_SUPPORTING_DOC_CONVERSIONS_KEY = ['admin', 'supporting-doc-conversions'] as const;

/** Failed + pending supporting-document conversions across all tenants; polls like the
 *  extraction runs above so the section stays live. */
export function useAdminSupportingDocConversions() {
  return useQuery({
    queryKey: ADMIN_SUPPORTING_DOC_CONVERSIONS_KEY,
    queryFn: getAdminSupportingDocConversions,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Retry a FAILED supporting-document conversion — re-arms it and re-dispatches. */
export function useRetrySupportingDocConversion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retrySupportingDocConversion,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_SUPPORTING_DOC_CONVERSIONS_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_STRATEGY_CONCURRENCY_KEY = ['admin', 'strategy-concurrency'] as const;

/** The strategy concurrency dial (effective cap / override / running count);
 *  polls so the running count stays live. */
export function useStrategyConcurrency() {
  return useQuery({
    queryKey: ADMIN_STRATEGY_CONCURRENCY_KEY,
    queryFn: getStrategyConcurrency,
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Set (number) or clear (null) the runtime strategy concurrency cap. Applies to
 *  the doorman + worker immediately — no redeploy. */
export function useSetStrategyConcurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setStrategyConcurrency,
    onSuccess: (res) => {
      toast.success(
        res.override != null
          ? `Strategy concurrency cap set to ${res.cap}.`
          : `Strategy concurrency cap reset to the default (${res.env_default}).`,
      );
      void qc.invalidateQueries({ queryKey: ADMIN_STRATEGY_CONCURRENCY_KEY });
    },
    onError: toastClientError,
  });
}

const ADMIN_STRATEGY_RUNS_KEY = ['admin', 'strategy-runs'] as const;

/** In-flight strategy runs across all tenants; polls so the panel stays live. */
export function useAdminStrategyRuns() {
  return useQuery({
    queryKey: ADMIN_STRATEGY_RUNS_KEY,
    queryFn: getAdminStrategyRuns,
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useForceFailStrategyRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: forceFailStrategyRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_STRATEGY_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

export function useRetryStrategyRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryStrategyRun,
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ADMIN_STRATEGY_RUNS_KEY });
    },
    onError: toastClientError,
  });
}

// ── Clinical protocol / report ops (kind-parameterized — the models are twins) ──

const clinicalConcurrencyKey = (kind: ClinicalAgentKind) =>
  ['admin', `${kind}-concurrency`] as const;
const clinicalRunsKey = (kind: ClinicalAgentKind) => ['admin', `${kind}-runs`] as const;

const CLINICAL_LABELS: Record<ClinicalAgentKind, string> = {
  'clinical-protocol': 'Clinical-protocol',
  'clinical-report': 'Clinical-report',
};

/** A clinical agent's concurrency dial; polls so the running count stays live. */
export function useClinicalConcurrency(kind: ClinicalAgentKind) {
  return useQuery({
    queryKey: clinicalConcurrencyKey(kind),
    queryFn: () => getClinicalConcurrency(kind),
    refetchInterval: 10_000,
    retry: false,
  });
}

/** Set (number) or clear (null) a clinical agent's runtime cap. Applies to the
 *  doorman + worker immediately — no redeploy. */
export function useSetClinicalConcurrency(kind: ClinicalAgentKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cap: number | null) => setClinicalConcurrency(kind, cap),
    onSuccess: (res) => {
      toast.success(
        res.override != null
          ? `${CLINICAL_LABELS[kind]} concurrency cap set to ${res.cap}.`
          : `${CLINICAL_LABELS[kind]} concurrency cap reset to the default (${res.env_default}).`,
      );
      void qc.invalidateQueries({ queryKey: clinicalConcurrencyKey(kind) });
    },
    onError: toastClientError,
  });
}

/** In-flight clinical runs across all tenants; polls so the panel stays live. */
export function useAdminClinicalRuns(kind: ClinicalAgentKind) {
  return useQuery({
    queryKey: clinicalRunsKey(kind),
    queryFn: () => getAdminClinicalRuns(kind),
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useForceFailClinicalRun(kind: ClinicalAgentKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => forceFailClinicalRun(kind, runId),
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: clinicalRunsKey(kind) });
    },
    onError: toastClientError,
  });
}

export function useRetryClinicalRun(kind: ClinicalAgentKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => retryClinicalRun(kind, runId),
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: clinicalRunsKey(kind) });
    },
    onError: toastClientError,
  });
}

// ── MCP Observability ────────────────────────────────────────────────────────

/** Fleet-wide MCP observability snapshot; re-fetches every 30 s automatically. */
export function useMcpObservability(windowSeconds: number) {
  return useQuery({
    queryKey: ['admin', 'mcp-observability', windowSeconds] as const,
    queryFn: () => getMcpObservability(windowSeconds),
    refetchInterval: 30_000,
    retry: false,
  });
}

// ── Corpus Observability ─────────────────────────────────────────────────────

/** Corpus ingest health snapshot; re-fetches every 30 s automatically.
 * staleTime matches the interval so tab focus doesn't add extra fetches
 * on top of it (the server caches for 60 s anyway). */
export function useCorpusObservability() {
  return useQuery({
    queryKey: ['admin', 'corpus-observability'] as const,
    queryFn: getCorpusObservability,
    refetchInterval: 30_000,
    staleTime: 30_000,
    retry: false,
  });
}

// --- Vault uploads → promote ---
export const ADMIN_VAULT_UPLOADS_KEY = ['admin', 'vaultUploads'] as const;

/** One offset page of training-track vault uploads across all tenants, optionally filtered
 *  by search text (filename/folder/company) and promoted state ('promoted'|'unpromoted'). */
export const ADMIN_VAULT_COMPANIES_KEY = ['admin', 'vaultCompanies'] as const;

/** Bulk-promote every promotable file in a folder into its linked product. */
export function usePromoteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { customerId: number; folderId: number; productId: number }) =>
      promoteFolder(args.customerId, args.folderId, args.productId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_VAULT_UPLOADS_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_VAULT_COMPANIES_KEY });
    },
  });
}

/** Paginated list of training-track companies with uploads (the panel's top level). */
export function useAdminVaultCompanies(offset: number, search = '', domain = '', promoted = '') {
  return useQuery({
    queryKey: [...ADMIN_VAULT_COMPANIES_KEY, { offset, search, domain, promoted }],
    queryFn: () => getAdminVaultCompanies(offset, 25, search, domain, promoted),
    retry: false,
  });
}

/** A company's uploads (used when a company is expanded), paginated so a company with many
 *  files isn't silently truncated. `enabled` gates the fetch to expanded rows so collapsed
 *  companies cost nothing; `domain`/`promoted` scope the results. */
// Load a whole company's promotable files in one shot (1000/page). A company rarely has more, so the
// folder tree loads complete — no confusing "Load more" that appends files into collapsed folders.
// The tree only renders rows for EXPANDED folders, so the DOM stays light even with many files.
export function useAdminCompanyUploads(customerId: number, enabled: boolean, domain = '', promoted = '') {
  return useInfiniteQuery({
    queryKey: [...ADMIN_VAULT_UPLOADS_KEY, { customerId, domain, promoted }],
    queryFn: ({ pageParam }) => getAdminVaultUploads(pageParam, 1000, '', promoted, customerId, domain),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.items.length;
      return next < last.total ? next : undefined;
    },
    enabled,
    retry: false,
  });
}

export function useAdminVaultUploads(offset: number, search = '', promoted = '') {
  return useQuery({
    queryKey: [...ADMIN_VAULT_UPLOADS_KEY, { offset, search, promoted }],
    queryFn: () => getAdminVaultUploads(offset, 25, search, promoted),
    retry: false,
  });
}

/** A company's products for the promote picker — fetched only once a row is chosen. */
export function useCompanyProducts(customerId: number | null) {
  return useQuery({
    queryKey: ['admin', 'companyProducts', customerId],
    queryFn: () => getCompanyProducts(customerId as number),
    enabled: customerId != null,
    retry: false,
  });
}

/** Promote a vault upload into a product's supporting docs (4xx guards toast). */
export function usePromoteVaultUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId, productId }: { uploadId: number; productId: number }) =>
      promoteVaultUpload(uploadId, productId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_VAULT_UPLOADS_KEY });
    },
    onError: toastClientError,
  });
}

/** Undo a promotion — remove the supporting doc from its product. */
export function useUnpromoteVaultUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: number) => unpromoteVaultUpload(uploadId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_VAULT_UPLOADS_KEY });
    },
    onError: toastClientError,
  });
}

/* ── Billing ─────────────────────────────────────────────────────────────── */

export const companyBillingKey = (customerId: number) =>
  [...ADMIN_COMPANIES_KEY, customerId, 'billing'] as const;

export function useCompanyBilling(customerId: number) {
  return useQuery({
    queryKey: companyBillingKey(customerId),
    queryFn: () => fetchCompanyBilling(customerId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useProvisionBilling(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: ProvisionBillingArgs) => provisionCompanyBilling(customerId, args),
    onSuccess: (row) => {
      toast.success(`Billing set up at ${row.rate_label ?? 'the agreed rate'}.`);
      void qc.invalidateQueries({ queryKey: companyBillingKey(customerId) });
      void qc.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
    },
  });
}

/* ── Billing health + override ───────────────────────────────────────────── */

export const BILLING_HEALTH_KEY = ['admin', 'billing-health'] as const;

export function useBillingHealth() {
  return useQuery({
    queryKey: BILLING_HEALTH_KEY,
    queryFn: getBillingHealth,
    // Admin surface: the 5-minute tier, focus-refetch off. This reads our own
    // database, not Stripe, so it is cheap — but nothing here changes minute to
    // minute either.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSetBillingOverride(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (until: string | null) => setBillingOverride(customerId, until),
    onSuccess: (row) => {
      toast.success(
        row.override_until
          ? 'Agent runs allowed until the date set.'
          : 'Override removed — the billing block applies again.',
      );
      void qc.invalidateQueries({ queryKey: companyBillingKey(customerId) });
      void qc.invalidateQueries({ queryKey: BILLING_HEALTH_KEY });
    },
  });
}
